import {
  wilmaSSOAPIUrl,
  wilmaSSOAppRegistration,
  wilmaSSODashboardUrl,
  resourceAPIURL,
  tokenStorageKey
} from "../config";
import { TokenPayload } from "../models/auth/TokenPayload";
import { IWilmaUser } from "../models/auth/WilmaUser";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";
import { handleResponse } from "../utils/handleResponse";

export function useAuthApi() {

  const getToken = async (shouldRedirect = true, retry = true) => {

    var currentToken = localStorage.getItem(tokenStorageKey);

    //check if current token is expired, if not return it
    if (currentToken) {
      // Decode the JWT token to check its expiration
      const tokenPayload: TokenPayload = JSON.parse(atob(currentToken.split('.')[1]));

      // Check if the token is still valid (2 minute buffer)
      if ((tokenPayload?.exp * 1000 - (1000 * 60 * 2)) > Date.now()) {
        // console.log("%c [Auth] - Using existing token", "color: #f0c002");
        return {
          success: true,
          data: {
            token: currentToken
          }
        };
      }
    }

    console.log("%c [Auth] - Getting new token", "color: #f0c002");

    var result: DefaultResponseWithData<{ token: string }> | undefined = undefined;

    try {
      const tokenRes = await fetch(`${wilmaSSOAPIUrl}/v1/auth/token`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          applicationRegistrationId: wilmaSSOAppRegistration,
        }),
      });

      result = await handleResponse<DefaultResponseWithData<{ token: string }>>(
        tokenRes
      );
    } catch (e: any) {
      if (e["response"] && e.response.status === 401) {
        if (retry) {
          console.log("%c [Auth] - Retrying token", "color: #f0c002");
          getToken(false, false); // Retry once
        }

        if (shouldRedirect) {
          window.location.assign(
            `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
          );
        }
      }
    } finally {
      // If we got a token, store it in localStorage
      localStorage.setItem(tokenStorageKey, result?.data.token || "");
      return result;
    }


  };

  // const getUsers = async (token: string) => {
  //   try {
  //     const usersRes = await fetch(`${wilmaSSOAPIUrl}/v1/customer/users`, {
  //       method: "GET",
  //       credentials: "include",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //     });

  //     return await handleResponse<DefaultResponseWithData<IWilmaUser[]>>(
  //       usersRes
  //     );
  //   } catch (e: any) {
  //     if (e["response"] && e.response.status === 401) {
  //       window.location.assign(
  //         `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
  //       );
  //     }
  //   }
  // };

  const getUsers = async (token: string, includeDeleted: boolean) => {

    //check for local storage first
    const cachedUsers = localStorage.getItem("wilmaUsers");
    const cachedUsersExp = localStorage.getItem("wilmaUsersExp");

    if (cachedUsers && cachedUsers !== "undefined" && cachedUsersExp && Date.now() < parseInt(cachedUsersExp)) {
      return JSON.parse(cachedUsers) as DefaultResponseWithData<IWilmaUser[]>;
    }

    var result: DefaultResponseWithData<IWilmaUser[]> | undefined = undefined;

    try {
      const usersRes = await fetch(`${resourceAPIURL}/api/user/all?includeDeleted=${includeDeleted}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      });

      result = await handleResponse<DefaultResponseWithData<IWilmaUser[]>>(
        usersRes
      );
    } catch (e: any) {
      console.error("error", e);
      // if (e["response"] && e.response.status === 401) {
      //   window.location.assign(
      //     `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
      //   );
      // }
    } finally {
      if (includeDeleted === false) {
        localStorage.setItem("wilmaUsers", JSON.stringify(result));
        localStorage.setItem("wilmaUsersExp", (Date.now() + 1000 * 60 * 10).toString()); // 10 minutes expiration
      }
      return result;
    }
  };

  const getUser = async (token: string) => {

    //check for local storage first
    const cachedUser = localStorage.getItem("wilmaUser");
    const cachedUserExp = localStorage.getItem("wilmaUserExp");

    if (cachedUser && cachedUser !== "undefined" && cachedUserExp && Date.now() < parseInt(cachedUserExp)) {
      return JSON.parse(cachedUser) as DefaultResponseWithData<IWilmaUser>;
    }

    const userRes = await fetch(`${resourceAPIURL}/api/user`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
    });

    var result = await handleResponse<DefaultResponseWithData<IWilmaUser>>(userRes);

    //save the user to localStorage
    localStorage.setItem("wilmaUser", JSON.stringify(result));
    localStorage.setItem("wilmaUserExp", (Date.now() + 1000 * 60 * 10).toString()); // 10 minutes expiration
    return result;
  };

  // true/ false and guid for user that its for
  const validatePin = async (pin: string, useAuthUser: boolean = true) => {
    const pinRes = await fetch(`${wilmaSSOAPIUrl}/v1/user/pin`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        pin: pin,
        useAuthUser: useAuthUser,
      }),
    });

    return await handleResponse<DefaultResponseWithData<string>>(pinRes);
  };

  // Generate pin for user, if username is set then it will generate a pin for that user
  // else it will generate a pin for the authed user
  const generatePin = async (username: string | null, password: string) => {
    const pinRes = await fetch(`${wilmaSSOAPIUrl}/v1/user/pin/generate`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: username,
        password: password,
      }),
    });

    return await handleResponse<DefaultResponseWithData<string>>(pinRes);
  };

  const createUser = async (user: IWilmaUser) => {
    try {
      const createRes = await fetch(`${wilmaSSOAPIUrl}/v1/user`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });

      return await handleResponse<DefaultResponseWithData<IWilmaUser>>(
        createRes
      );
    } catch (e: any) {
      //   if (e["response"] && e.response.status === 401) {
      //     window.location.assign(
      //       `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
      //     );
      //   }

      //   throw e;
    }
  };

  const updateUser = async (user: IWilmaUser) => {
    try {
      const updateRes = await fetch(`${wilmaSSOAPIUrl}/v1/user/${user.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });

      return await handleResponse<DefaultResponseWithData<IWilmaUser>>(
        updateRes
      );
    } catch (e: any) {
      // if (e["response"] && e.response.status === 401) {
      //   window.location.assign(
      //     `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
      //   );
      // }

      // throw e;
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      const deleteRes = await fetch(`${wilmaSSOAPIUrl}/v1/user/${userId}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      return await handleResponse<DefaultResponse>(deleteRes);
    } catch (e: any) {
      // if (e["response"] && e.response.status === 401) {
      //   window.location.assign(
      //     `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
      //   );
      // }

      // throw e;
    }
  };

  const logout = async () => {
    try {
      const logoutRes = await fetch(`${wilmaSSOAPIUrl}/v1/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          applicationRegistrationId: wilmaSSOAppRegistration,
        }),
      });

      return await handleResponse<DefaultResponse>(logoutRes);
    } catch (e: any) {
      if (e["response"] && e.response.status === 401) {
        window.location.assign(
          `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
        );
      }

      throw e;
    } finally {
      const hiddenAssemblies = localStorage.getItem("jobPartsHiddenAssemblies");
      localStorage.clear();
      if (hiddenAssemblies !== null) {
        localStorage.setItem("jobPartsHiddenAssemblies", hiddenAssemblies);
      }
    }
  };

  return {
    getToken,
    getUsers,
    getUser,
    validatePin,
    generatePin,
    createUser,
    updateUser,
    deleteUser,
    logout,
  };
}
